import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { Link, NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';
import { apiGet, apiPatch, isUnauthorizedApiError, SUPER_TOKEN_KEY } from '../../api/client';
import type { OrgDetail } from './superOrgTypes';

export type SuperOrgDetailOutletContext = {
  org: OrgDetail;
  token: string;
  reload: () => Promise<void>;
  setPageErr: (msg: string) => void;
};

const tabBase: CSSProperties = {
  display: 'inline-block',
  padding: '10px 16px',
  borderRadius: 8,
  textDecoration: 'none',
  fontSize: 14,
  fontWeight: 500,
  color: '#64748b',
  border: '1px solid transparent',
};
const tabActive: CSSProperties = {
  ...tabBase,
  color: '#312e81',
  background: '#f5f3ff',
  borderColor: '#ddd6fe',
  fontWeight: 600,
};

const hdrBtn: CSSProperties = {
  padding: '8px 14px',
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  textDecoration: 'none',
  display: 'inline-block',
  textAlign: 'center',
};

export function SuperOrgDetailLayout() {
  const { orgId } = useParams<{ orgId: string }>();
  const nav = useNavigate();
  const token = localStorage.getItem(SUPER_TOKEN_KEY);
  const [org, setOrg] = useState<OrgDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [pageErr, setPageErr] = useState('');
  const [fatalErr, setFatalErr] = useState<string | null>(null);
  const [savingDemo, setSavingDemo] = useState(false);

  const reload = useCallback(async () => {
    if (!token || !orgId) return;
    setPageErr('');
    const data = await apiGet<OrgDetail>(`/api/super/organizations/${orgId}`, token);
    setOrg(data);
  }, [token, orgId]);

  useEffect(() => {
    if (!token) {
      nav('/super/login', { replace: true });
      return;
    }
    if (!orgId) {
      nav('/super/orgs', { replace: true });
      return;
    }
    let cancelled = false;
    setLoading(true);
    setOrg(null);
    setFatalErr(null);
    void (async () => {
      try {
        await reload();
      } catch (e) {
        if (cancelled) return;
        if (isUnauthorizedApiError(e)) {
          localStorage.removeItem(SUPER_TOKEN_KEY);
          nav('/super/login', { replace: true });
          return;
        }
        setFatalErr((e as Error).message || 'No se pudo cargar la empresa');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, orgId, nav, reload]);

  const goToSuperLogin = () => {
    localStorage.removeItem(SUPER_TOKEN_KEY);
    nav('/super/login', { replace: true });
  };

  const toggleDemo = async () => {
    if (!token || !orgId || !org) return;
    setSavingDemo(true);
    setPageErr('');
    try {
      await apiPatch(`/api/super/organizations/${orgId}`, { isDemo: !org.isDemo }, token, {
        successMessage: org.isDemo ? 'Ya no está marcada como demo' : 'Marcada como empresa de demo',
      });
      await reload();
    } catch (e) {
      setPageErr((e as Error).message);
    } finally {
      setSavingDemo(false);
    }
  };

  if (!token) return null;

  if (fatalErr) {
    return (
      <div style={{ flex: 1, padding: 32, maxWidth: 560, margin: '0 auto' }}>
        <p style={{ color: '#b91c1c', marginBottom: 16 }}>{fatalErr}</p>
        <Link
          to="/super/orgs"
          style={{
            ...hdrBtn,
            background: '#fff',
            color: '#7c3aed',
            border: '1px solid #ddd6fe',
          }}
        >
          Volver a empresas
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ flex: 1, padding: 32, color: '#64748b' }}>
        Cargando empresa…
      </div>
    );
  }

  if (!org || !orgId) {
    return null;
  }

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: '#f8fafc' }}>
      <header
        style={{
          background: '#fff',
          borderBottom: '1px solid #e2e8f0',
          padding: '16px 24px',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.05 }}>
            Empresa · super admin
          </div>
          <h1 style={{ margin: '4px 0 0', fontSize: 20, color: '#0f172a' }}>
            {org.name}
            <span style={{ fontWeight: 500, color: '#64748b', marginLeft: 8 }}>/{org.slug}</span>
            {org.isDemo ?
              <span
                style={{
                  marginLeft: 10,
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: 999,
                  background: '#ecfdf5',
                  color: '#047857',
                  border: '1px solid #a7f3d0',
                  verticalAlign: 'middle',
                }}
              >
                Demo
              </span>
            : null}
          </h1>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b' }}>
            Creada {new Date(org.createdAt).toLocaleString()} · Agentes: {org.counts.agents} · Conversaciones:{' '}
            {org.counts.conversations}
          </p>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#334155' }}>
            <input
              type="checkbox"
              checked={Boolean(org.isDemo)}
              disabled={savingDemo}
              onChange={() => void toggleDemo()}
            />
            Empresa de demo
          </label>
          <Link
            to="/super/orgs"
            style={{
              ...hdrBtn,
              background: '#fff',
              color: '#7c3aed',
              border: '1px solid #ddd6fe',
            }}
          >
            Todas las empresas
          </Link>
          <button
            type="button"
            onClick={goToSuperLogin}
            style={{
              ...hdrBtn,
              border: '1px solid rgba(248, 113, 113, 0.5)',
              background: 'rgba(127, 29, 29, 0.08)',
              color: '#b91c1c',
            }}
          >
            Ir a login de plataforma
          </button>
        </div>
      </header>

      <div style={{ padding: '0 24px', borderBottom: '1px solid #e2e8f0', background: '#fff' }}>
        <nav style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4, marginBottom: -1 }}>
          <NavLink
            to="usuarios"
            end
            style={({ isActive }) => (isActive ? tabActive : tabBase)}
          >
            Usuarios
          </NavLink>
          <NavLink to="uso-demo" style={({ isActive }) => (isActive ? tabActive : tabBase)}>
            Uso demo
          </NavLink>
          <NavLink to="consumo" style={({ isActive }) => (isActive ? tabActive : tabBase)}>
            Consumo
          </NavLink>
          <NavLink to="modelos" style={({ isActive }) => (isActive ? tabActive : tabBase)}>
            Modelos
          </NavLink>
          <NavLink to="pwa" style={({ isActive }) => (isActive ? tabActive : tabBase)}>
            PWA
          </NavLink>
          <NavLink to="marca" style={({ isActive }) => (isActive ? tabActive : tabBase)}>
            Marca
          </NavLink>
          <NavLink to="roles" style={({ isActive }) => (isActive ? tabActive : tabBase)}>
            Roles
          </NavLink>
          <NavLink to="transcripcion" style={({ isActive }) => (isActive ? tabActive : tabBase)}>
            Transcripción
          </NavLink>
        </nav>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', maxWidth: 900, width: '100%', margin: '0 auto', padding: 24 }}>
        {pageErr ? <p style={{ color: '#b91c1c', marginTop: 0 }}>{pageErr}</p> : null}
        <Outlet
          context={
            { org, token, reload, setPageErr } satisfies SuperOrgDetailOutletContext
          }
        />
      </div>
    </div>
  );
}
